import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Settings, Key, Check, X, AlertCircle, Loader2,
  RefreshCw, Save, ExternalLink, Database
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Status Badge Component
function StatusBadge({ connected, label }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
      connected 
        ? 'bg-green-100 text-green-700 border border-green-200' 
        : 'bg-red-100 text-red-700 border border-red-200'
    }`}>
      {connected ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
      {label || (connected ? 'Connected' : 'Not Connected')}
    </span>
  );
}

// Settings Card Component
function SettingsCard({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <h2 className="font-bold text-slate-900">{title}</h2>
      </div>
      <div className="p-6">
        {children}
      </div>
    </div>
  );
}

// Main Settings Page
export default function SettingsPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form state
  const [freshdeskDomain, setFreshdeskDomain] = useState('');
  const [freshdeskApiKey, setFreshdeskApiKey] = useState('');
  
  // Freshdesk companies
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  
  const fetchStatus = async () => {
    try {
      const [statusRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/status`),
        axios.get(`${API_URL}/api/settings`)
      ]);
      
      setStatus(statusRes.data);
      setFreshdeskDomain(settingsRes.data.freshdesk_domain || '');
    } catch (err) {
      setError('Failed to load settings');
    }
    setLoading(false);
  };
  
  useEffect(() => {
    fetchStatus();
  }, []);
  
  const handleSaveFreshdesk = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const params = new URLSearchParams();
      if (freshdeskDomain) params.append('domain', freshdeskDomain);
      if (freshdeskApiKey) params.append('api_key', freshdeskApiKey);
      
      await axios.put(`${API_URL}/api/settings/freshdesk?${params.toString()}`);
      
      setSuccess('Freshdesk settings updated successfully');
      setFreshdeskApiKey(''); // Clear API key field
      fetchStatus(); // Refresh status
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update settings');
    }
    
    setSaving(false);
  };
  
  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const response = await axios.get(`${API_URL}/api/freshdesk/companies`);
      setCompanies(response.data.companies || []);
    } catch (err) {
      setError('Failed to fetch Freshdesk companies');
    }
    setLoadingCompanies(false);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div data-testid="settings-page" className="max-w-2xl mx-auto space-y-6">
      {/* Notifications */}
      {error && (
        <div className="p-4 rounded-lg bg-red-50 text-red-600 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}
      
      {success && (
        <div className="p-4 rounded-lg bg-green-50 text-green-600 flex items-center gap-2">
          <Check className="w-5 h-5" />
          {success}
        </div>
      )}
      
      {/* System Status */}
      <SettingsCard title="System Status" icon={Settings}>
        <div className="space-y-4">
          {/* SQLite Status */}
          <div className="flex items-center justify-between py-3 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <Database className="w-5 h-5 text-slate-400" />
              <div>
                <p className="font-medium text-slate-900">SQLite Database</p>
                <p className="text-sm text-slate-500 font-mono text-xs">{status?.sqlite?.database}</p>
              </div>
            </div>
            <StatusBadge connected={status?.sqlite?.connected} />
          </div>
          
          {/* Key Vault Status */}
          <div className="flex items-center justify-between py-3 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <Key className="w-5 h-5 text-slate-400" />
              <div>
                <p className="font-medium text-slate-900">Azure Key Vault</p>
                <p className="text-sm text-slate-500 font-mono text-xs truncate max-w-xs">
                  {status?.keyvault?.url || 'Not configured'}
                </p>
              </div>
            </div>
            <StatusBadge 
              connected={status?.keyvault?.connected} 
              label={status?.keyvault?.configured ? (status?.keyvault?.connected ? 'Connected' : 'Auth Failed') : 'Not Configured'}
            />
          </div>
          
          {/* Freshdesk Status */}
          <div className="flex items-center justify-between py-3 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <ExternalLink className="w-5 h-5 text-slate-400" />
              <div>
                <p className="font-medium text-slate-900">Freshdesk</p>
                <p className="text-sm text-slate-500">
                  {status?.freshdesk?.domain ? `${status.freshdesk.domain}.freshdesk.com` : 'Not configured'}
                </p>
              </div>
            </div>
            <StatusBadge connected={status?.freshdesk?.connected} />
          </div>
          
          {/* Site24x7 Status */}
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <RefreshCw className="w-5 h-5 text-slate-400" />
              <div>
                <p className="font-medium text-slate-900">Site24x7 Monitoring</p>
                <p className="text-sm text-slate-500">
                  {status?.site24x7?.connected ? `${status.site24x7.groups} monitor groups` : status?.site24x7?.configured ? 'Configured but not connected' : 'Not configured'}
                </p>
              </div>
            </div>
            <StatusBadge 
              connected={status?.site24x7?.connected} 
              label={status?.site24x7?.connected ? 'Connected' : status?.site24x7?.configured ? 'Auth Failed' : 'Not Configured'}
            />
          </div>
        </div>
        
        <button
          data-testid="refresh-status-btn"
          onClick={fetchStatus}
          className="mt-4 flex items-center gap-2 text-sm text-primary font-medium hover:text-primary-600"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh Status
        </button>
      </SettingsCard>
      
      {/* Freshdesk Configuration */}
      <SettingsCard title="Freshdesk Configuration" icon={ExternalLink}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Freshdesk Domain
            </label>
            <div className="flex items-center gap-2">
              <input
                data-testid="freshdesk-domain-input"
                type="text"
                value={freshdeskDomain}
                onChange={(e) => setFreshdeskDomain(e.target.value)}
                placeholder="yourcompany"
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary"
              />
              <span className="text-slate-500">.freshdesk.com</span>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              API Key
              <span className="text-slate-400 font-normal ml-1">(leave empty to keep current)</span>
            </label>
            <input
              data-testid="freshdesk-api-key-input"
              type="password"
              value={freshdeskApiKey}
              onChange={(e) => setFreshdeskApiKey(e.target.value)}
              placeholder="Enter new API key"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary"
            />
            <p className="mt-1 text-xs text-slate-500">
              Find your API key in Freshdesk → Profile Settings → API Key
            </p>
          </div>
          
          <button
            data-testid="save-freshdesk-btn"
            onClick={handleSaveFreshdesk}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Freshdesk Settings
          </button>
        </div>
      </SettingsCard>
      
      {/* Freshdesk Companies Lookup */}
      <SettingsCard title="Freshdesk Companies" icon={Database}>
        <p className="text-sm text-slate-600 mb-4">
          Lookup company IDs from your Freshdesk account to use when adding customers.
        </p>
        
        <button
          data-testid="fetch-companies-btn"
          onClick={fetchCompanies}
          disabled={loadingCompanies || !status?.freshdesk?.connected}
          className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
        >
          {loadingCompanies ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Fetch Companies
        </button>
        
        {!status?.freshdesk?.connected && (
          <p className="mt-2 text-sm text-slate-500">
            Configure Freshdesk connection above to fetch companies.
          </p>
        )}
        
        {companies.length > 0 && (
          <div className="mt-4 max-h-64 overflow-y-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50">
                <tr>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">ID</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Company Name</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company, idx) => (
                  <tr key={company.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                    <td className="py-2 px-3 font-mono text-xs">{company.id}</td>
                    <td className="py-2 px-3">{company.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SettingsCard>
      
      {/* About */}
      <div className="text-center text-sm text-slate-500 py-4">
        <p>Tekpro Report Generator v1.0.0</p>
        <p>© 2026 Tekkonnectpro IT Services</p>
      </div>
    </div>
  );
}
